import {
  FolderTwoTone,
  HomeTwoTone,
  LeftOutlined,
  ReadOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Button, Layout, Menu } from 'antd';
import { ItemType, MenuItemType } from 'antd/lib/menu/interface';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import Colors from '../const/colors';

const { Sider } = Layout;

function getColor(condition: boolean) {
  return condition ? Colors.BleuHellio : Colors.GrisClair;
}

interface SideMenuProps {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  isImpersonated: boolean;
}

const SideMenu: React.FC<SideMenuProps> = ({
  collapsed,
  setCollapsed,
  isImpersonated,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const handleCollapse = (value: boolean) => {
    setCollapsed(value);
  };

  const getActiveRoute = () => {
    if (location.pathname === '/') return '/';
    if (location.pathname.startsWith('/dossiers')) return '/dossiers';
    if (location.pathname.startsWith('/ressources')) return '/ressources';
    return location.pathname;
  };

  const activeRoute = getActiveRoute();

  const menuItems: ItemType<MenuItemType>[] = [
    {
      key: '/',
      icon: <HomeTwoTone twoToneColor={getColor(activeRoute === '/')} />,
      label: t('menu:home'),
    },
    {
      key: '/dossiers',
      icon: (
        <FolderTwoTone twoToneColor={getColor(activeRoute === '/dossiers')} />
      ),
      label: t('menu:dossiers'),
    },
    {
      key: '/ressources',
      icon: <ReadOutlined color={getColor(activeRoute === '/ressources')} />,
      label: t('menu:ressources'),
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    history.push(key);
  };

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={handleCollapse}
      width={205}
      collapsedWidth={80}
      trigger={null}
      className={clsx(
        'bg-white border-r border-gray-200 fixed left-0 h-[calc(100vh-64px)] overflow-y-auto z-40',
        {
          'top-28': isImpersonated,
          'top-16': !isImpersonated,
        },
      )}
    >
      <Menu
        mode="inline"
        selectedKeys={[activeRoute]}
        items={menuItems}
        onClick={handleMenuClick}
        className="side-menu border-r-0 pt-4"
      />

      <div className="absolute bottom-14 w-full">
        <Button
          type="primary"
          shape="circle"
          icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
          size="small"
          className="absolute -right-0 top-1/2 -translate-y-1/2 !rounded-r-none"
          onClick={() => handleCollapse(!collapsed)}
          aria-label={
            collapsed ? t('menu:expand_sidebar') : t('menu:collapse_sidebar')
          }
        />
      </div>
    </Sider>
  );
};

export default SideMenu;
